import React, {
    useContext, useEffect, useMemo, useRef, useState 
} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
    Box, Slide, Typography, useTheme 
} from '@mui/material';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import ProgressBar from '../../components/progressBar';
import {
    DiagnosticFormContext 
} from '../../../../contexts/form';
import {
    TypographyVariants, TypographyWeights 
} from '../../../../figmaTheme';

type Props = {
    position: number
};

export const PillarsIntro = ({
    position
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const [textHeight, setTextHeight] = useState(0);
    const [skipAnimation, setSkipAnimation] = useState(false);
    const useStyles = useMemo(() => makeStyles((theme) => ({
        scrollableContainer: {
            overflow: 'scroll',
            scrollbarWidth: 'none',
            minHeight: '100%',
            width: '100vw',
            maxWidth: '692px',
            transform: 'translate(-24px, 0)',
            padding: '0 24px'
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
        },
        pillarTitle: {
            width: '100%',
            textAlign: 'center',
            fontFamily: 'Poppins',
            fontSize: '20px',
            fontWeight: TypographyWeights.BOLD,
            lineHeight: '28px',
            margin: 0
        },
        title: {
            marginBottom: '16px'
        },
        summaryContainer: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            rowGap: '34px',
            marginTop: '28px',
            alignItems: 'center'
        },
        summary: {
            display: 'flex',
            flexDirection: 'column',
            rowGap: '8px',
            width: '100%'
        },
        item: {
            display: 'grid',
            gridTemplateColumns: '32px 1fr',
            width: '100%',
            alignItems: 'center',
            overflow: 'hidden',
            borderRadius: '32px 12px 12px 32px'
        },
        itemValue: {
            left: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '64px',
            height: '64px',
            backgroundColor: theme.palette.primary.main,
            border: `4px solid ${theme.palette.white}`,
            borderRadius: '50%',
            zIndex: 1,
            transform: 'scale(0)',
            animation: `$grow ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 0.5}s`
        },
        planningValue: {
            position: 'absolute'
        },
        itemTextContainer: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            paddingLeft: '32px',
            width: '100%',
            minHeight: '46px',
            borderRadius: '12px',
            backgroundColor: theme.palette.primary['100'],
            transform: 'translate(-100vw, 0)',
            animation: `$slideLeft ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s`
        },
        planningTextContainer: {
            marginLeft: '32px'
        },
        itemText: {
            marginLeft: '28px'
        },
        summaryText: {
            fontFamily: 'Poppins',
            fontSize: '16px',
            lineHeight: '24px',
            textAlign: 'center',
            padding: '0 16px',
            margin: 0,
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2}s`
        },
        fadeIn: {
            opacity: 0,
            animation: '$fadeIn 0.5s forwards 2s'
        },
        fadeOut: {
            opacity: 1,
            animation: '$fadeOut 0.5s forwards 0s'
        },
        shift: {
            animation: '$shift 2s forwards 0s'
        },
        hide: {
            animation: '$hide 0.5s forwards 0s'
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: 0 
            },
            '100%': {
                opacity: 1 
            },
        },
        '@keyframes fadeOut': {
            '0%': {
                opacity: 1 
            },
            '100%': {
                opacity: 0 
            },
        },
        '@keyframes grow': {
            '0%': {
                transform: 'scale(0)' 
            },
            '100%': {
                transform: 'scale(1)' 
            },
        },
        '@keyframes slideLeft': {
            '0%': {
                transform: 'translate(-100vw, 0)' 
            },
            '100%': {
                transform: 'translate(0)' 
            },
        },
        '@keyframes shift': {
            '0%': {
                transform: 'translate(0)' 
            },
            '50%': {
                transform: 'translate(calc(50vw - 56px), 0)' 
            },
            '100%': {
                transform: `translate(calc(50vw - 56px), -${textHeight + 21}px)` 
            },
        },
        '@keyframes hide': {
            '0%': {
                transform: 'translate(0)',
                opacity: 1 
            },
            '100%': {
                transform: 'translate(-100vw, 0)',
                opacity: 0 
            },
        },
    })), [textHeight, skipAnimation]);

    const classes = useStyles();
    const theme = useTheme();

    const onClick = () => {
        if (!skipAnimation) setSkipAnimation(true);
        else setStep(1);
    };

    const [step, setStep] = useState(0);
    useEffect(() => {
        if (step === 1) {
            setTimeout(() => {
                formContext.goNext();
            }, 2000);
        }
    }, [step]);

    const textBox = useRef(null);

    useEffect(() => {
        if (!textBox.current) return;

        setTextHeight(textBox.current.clientHeight);
    }, [textBox.current]);

    useEffect(() => {
        setTimeout(() => {
            setSkipAnimation(true);
        }, 1500);
    });

    return (
        <CardContainer
            sectionKey={'plan'}
            goBack={step === 0 && formContext.goBack}
            goNext={step === 0 && onClick}
            hasGradient
        >
            <Card>
                <ProgressBar
                    currentPosition={position}
                    sectionKey={'plan'}
                />
                <Box className={classes.scrollableContainer}>
                    <Slide
                        in={true}
                        direction={formContext.screenDirection}
                        timeout={500}
                        mountOnEnter
                        unmountOnExit
                    >
                        <Box>
                            <Box
                                ref={textBox}
                                className={`${classes.content} ${step === 1 ? classes.hide : null}`}
                            >
                                <Typography
                                    variant={TypographyVariants.H3}
                                    className={classes.title}
                                    dangerouslySetInnerHTML={{
                                        __html: 'Now, let\'s talk about how we\'ll get there:' 
                                    }}
                                />
                                <Typography
                                    variant={TypographyVariants.H6}
                                    dangerouslySetInnerHTML={{
                                        __html: 'Sunnyside\'s evidence-based program leverages 4 key pillars to achieve sustained habit change.' 
                                    }}
                                />
                            </Box>
                            <Box className={classes.summaryContainer}>
                                <Box className={classes.summary}>
                                    <Box
                                        className={classes.item}
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            overflow: step === 0 ? 'hidden' : 'visible'
                                        }}
                                    >
                                        <Box
                                            className={`${classes.itemValue} ${classes.planningValue} ${step === 1 ? classes.shift : null}`}
                                        >
                                            <svg
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <g clipPath="url(#clip0_4414_15404)">
                                                    <path
                                                        d="M4.70045 0.722656C2.27017 0.722656 0.300049 2.69278 0.300049 5.12306V6.7732C0.300049 9.20348 2.27018 11.1736 4.70045 11.1736H6.3506C8.78087 11.1736 10.751 9.20347 10.751 6.7732V5.12305C10.751 2.69278 8.78087 0.722656 6.35059 0.722656H4.70045Z"
                                                        fill={theme.palette.white}
                                                    />
                                                    <path
                                                        d="M16.8008 12.8276C14.3705 12.8276 12.4004 14.7977 12.4004 17.228V18.8781C12.4004 21.3084 14.3705 23.2785 16.8008 23.2785H18.4509C20.8812 23.2785 22.8513 21.3084 22.8513 18.8781V17.228C22.8513 14.7977 20.8812 12.8276 18.4509 12.8276H16.8008Z"
                                                        fill={theme.palette.white}
                                                    />
                                                    <path
                                                        d="M4.70045 12.8276C2.27017 12.8276 0.300049 14.7977 0.300049 17.228V18.8781C0.300049 21.3084 2.27018 23.2785 4.70045 23.2785H6.3506C8.78087 23.2785 10.751 21.3084 10.751 18.8781V17.228C10.751 14.7977 8.78087 12.8276 6.35059 12.8276H4.70045Z"
                                                        fill={theme.palette.white}
                                                    />
                                                    <path
                                                        fillRule="evenodd"
                                                        clipRule="evenodd"
                                                        d="M23.3707 2.40459C23.8098 2.84377 23.8098 3.55582 23.3707 3.995L18.6654 8.70029C17.787 9.57866 16.3629 9.57865 15.4845 8.70029L14.0796 7.2953C13.6404 6.85612 13.6404 6.14407 14.0796 5.70489C14.5187 5.2657 15.2308 5.2657 15.67 5.70489L17.075 7.10988L21.7802 2.40459C22.2194 1.96541 22.9315 1.96541 23.3707 2.40459Z"
                                                        fill={theme.palette.white}
                                                    />
                                                </g>
                                                <defs>
                                                    <clipPath id="clip0_4414_15404">
                                                        <rect
                                                            width="24"
                                                            height="24"
                                                            fill={theme.palette.white} 
                                                        />
                                                    </clipPath>
                                                </defs>
                                            </svg>
                                        </Box>
                                        <Box className={`${classes.itemTextContainer} ${classes.planningTextContainer} ${step === 1 ? classes.fadeOut : null}`}>
                                            <Typography
                                                variant={TypographyVariants.H5}
                                                className={classes.itemText}
                                                dangerouslySetInnerHTML={{
                                                    __html: 'Planning & Goal Setting' 
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                    <Box
                                        className={`${classes.item} ${step === 1 ? classes.fadeOut : null}`}
                                        style={{
                                            marginTop: '8px' 
                                        }}
                                    >
                                        <Box className={classes.itemValue}>
                                            <svg
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24" 
                                                fill="none" 
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path 
                                                    fillRule="evenodd" 
                                                    clipRule="evenodd" 
                                                    d="M0 10.8837C0 4.79201 5.45773 0 12 0C18.5423 0 24 4.79201 24 10.8837C24 14.3 21.9367 17.2059 19.5402 19.3866C17.1295 21.5804 14.2493 23.1648 12.3109 23.9401C12.053 24.0433 11.7607 24.0118 11.5306 23.856C11.3005 23.7003 11.1628 23.4406 11.1628 23.1628V21.1786C8.44782 20.9809 5.81108 19.8393 3.78961 18.1548C1.56522 16.3011 0 13.712 0 10.8837ZM6.97675 12.5581C7.9015 12.5581 8.65116 11.8084 8.65116 10.8837C8.65116 9.95898 7.9015 9.20929 6.97675 9.20929C6.05199 9.20929 5.30233 9.95898 5.30233 10.8837C5.30233 11.8084 6.05199 12.5581 6.97675 12.5581ZM12 12.5581C12.9247 12.5581 13.6744 11.8084 13.6744 10.8837C13.6744 9.95898 12.9247 9.20929 12 9.20929C11.0753 9.20929 10.3256 9.95898 10.3256 10.8837C10.3256 11.8084 11.0753 12.5581 12 12.5581ZM18.6977 10.8837C18.6977 11.8084 17.948 12.5581 17.0233 12.5581C16.0985 12.5581 15.3488 11.8084 15.3488 10.8837C15.3488 9.95898 16.0985 9.20929 17.0233 9.20929C17.948 9.20929 18.6977 9.95898 18.6977 10.8837Z" 
                                                    fill={theme.palette.white}
                                                />
                                            </svg>
                                        </Box>
                                        <Box className={classes.itemTextContainer}>
                                            <Typography
                                                variant={TypographyVariants.H5}
                                                className={classes.itemText} 
                                                dangerouslySetInnerHTML={{
                                                    __html: 'Tracking & Daily Nudges' 
                                                }} 
                                            />
                                        </Box>
                                    </Box>
                                    <Box className={`${classes.item} ${step === 1 ? classes.fadeOut : null}`}>
                                        <Box className={classes.itemValue}>
                                            <svg 
                                                width="24" 
                                                height="24" 
                                                viewBox="0 0 24 24" 
                                                fill="none" 
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path 
                                                    fillRule="evenodd" 
                                                    clipRule="evenodd" 
                                                    d="M18.2006 0.770488H17.9471C18.0542 1.07277 18.103 1.41409 18.103 1.76493C18.103 3.64671 16.5626 5.16742 14.6907 5.16742H9.30911C7.42757 5.16742 5.90662 3.64671 5.90662 1.76493C5.90662 1.41409 5.95542 1.07277 6.06264 0.770488H5.7994C4.17123 0.770488 2.84546 2.08673 2.84546 3.71479V20.737C2.84546 22.375 4.17123 23.7008 5.7994 23.7008H18.2006C19.8286 23.7008 21.1545 22.375 21.1545 20.737V3.71479C21.1545 2.08673 19.8286 0.770488 18.2006 0.770488ZM17.0013 19.0894H11.8538C11.3175 19.0894 10.8886 18.6605 10.8886 18.1242C10.8886 17.588 11.3175 17.1493 11.8538 17.1493H17.0013C17.5278 17.1493 17.9665 17.588 17.9665 18.1242C17.9665 18.6605 17.5278 19.0894 17.0013 19.0894ZM17.0013 13.2691H11.8538C11.3175 13.2691 10.8886 12.8402 10.8886 12.3039C10.8886 11.7677 11.3175 11.329 11.8538 11.329H17.0013C17.5278 11.329 17.9665 11.7677 17.9665 12.3039C17.9665 12.8402 17.5278 13.2691 17.0013 13.2691ZM9.00312 18.1619C9.00312 19.0115 8.31433 19.7003 7.46466 19.7003C6.61499 19.7003 5.9262 19.0115 5.9262 18.1619C5.9262 17.3122 6.61499 16.6234 7.46466 16.6234C8.31433 16.6234 9.00312 17.3122 9.00312 18.1619ZM7.46466 13.7742C8.31433 13.7742 9.00312 13.0854 9.00312 12.2357C9.00312 11.386 8.31433 10.6972 7.46466 10.6972C6.61499 10.6972 5.9262 11.386 5.9262 12.2357C5.9262 13.0854 6.61499 13.7742 7.46466 13.7742Z" 
                                                    fill={theme.palette.white} 
                                                />
                                                <path 
                                                    d="M16.1559 1.7656C16.1559 2.57459 15.5 3.23042 14.691 3.23042H9.30715C8.49816 3.23042 7.84234 2.5746 7.84234 1.7656C7.84234 0.956605 8.49816 0.300781 9.30715 0.300781H14.691C15.5 0.300781 16.1559 0.956602 16.1559 1.7656Z" 
                                                    fill={theme.palette.white} 
                                                />
                                            </svg>
                                        </Box>
                                        <Box className={classes.itemTextContainer}>
                                            <Typography
                                                variant={TypographyVariants.H5}
                                                className={classes.itemText} 
                                                dangerouslySetInnerHTML={{
                                                    __html: 'Coaching' 
                                                }} 
                                            />
                                        </Box>
                                    </Box>
                                    <Box className={`${classes.item} ${step === 1 ? classes.fadeOut : null}`}>
                                        <Box className={classes.itemValue}>
                                            <svg 
                                                width="24" 
                                                height="24" 
                                                viewBox="0 0 24 24" 
                                                fill="none" 
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path 
                                                    d="M16.0086 8.88306C15.7843 9.1779 15.4592 9.36747 15.0922 9.41748C14.7255 9.46749 14.3613 9.3715 14.0665 9.14765L11.8346 7.45058L10.1997 6.16987C9.34416 5.49962 8.14496 5.488 7.27655 6.14154L1.68146 10.3522C1.23872 10.6854 0.600042 10.4964 0.477645 9.95594C0.309453 9.21501 0.29453 8.67736 0.300983 8.42285V8.40107C0.30058 8.39179 0.300177 8.37929 0.300177 8.36477C0.30179 7.55608 0.462318 6.76594 0.777728 6.01654C1.09112 5.27158 1.54125 4.60325 2.11641 4.0301C2.7085 3.44002 3.40628 2.9782 4.19077 2.65714C5.01237 2.32076 5.90495 2.15015 6.84352 2.15015H6.85965C8.46695 2.15337 9.95043 2.64867 11.3899 3.66226L11.4036 3.67188C11.4345 3.6936 11.465 3.71513 11.4956 3.73728C11.5565 3.78124 11.6247 3.83085 11.6981 3.8853C11.7913 3.95347 11.8933 4.02889 12.003 4.11037C13.3239 5.09088 15.7157 6.91961 15.744 6.94139C16.3526 7.40362 16.4712 8.27483 16.0086 8.88306Z" 
                                                    fill={theme.palette.white}
                                                />
                                                <path 
                                                    d="M1.97028 11.627L7.30077 7.73697C8.1602 7.10979 9.32998 7.12329 10.1747 7.77016L13.5783 10.3766C14.732 11.26 16.3834 11.0409 17.2668 9.88729C18.1502 8.73364 17.9312 7.08226 16.7775 6.19883L13.3442 3.56967C13.2106 3.46739 13.2212 3.26314 13.3679 3.18087C14.8281 2.36213 16.192 2.17153 17.1426 2.17153H17.166C18.1106 2.17355 19.0081 2.34779 19.8329 2.68942C20.6158 3.0137 21.3119 3.47835 21.9016 4.07085C22.4739 4.64641 22.9216 5.31636 23.2318 6.06293C23.544 6.81354 23.7013 7.60449 23.6997 8.41237C23.6997 8.42689 23.6997 8.4398 23.6989 8.44867V8.47126C23.7041 8.72536 23.6872 9.26261 23.5158 10.0035C23.281 11.0171 22.8458 12.0497 22.2231 13.0721C21.3482 14.5088 20.2265 15.7499 18.7939 16.8667C17.1039 18.184 16.1504 18.862 14.3588 20.0212C13.974 20.2701 12.8471 21.0159 11.9698 21.5979C11.3176 22.0303 10.4593 21.8939 9.97289 21.2813C9.71839 20.9602 9.60787 20.5601 9.66192 20.1543C9.71597 19.7482 9.92732 19.3908 10.2572 19.148L11.9734 17.8839C12.1348 17.7654 12.1695 17.5383 12.0509 17.377C11.9799 17.2801 11.8698 17.2285 11.7581 17.2285C11.6834 17.2285 11.6084 17.2515 11.5439 17.2987L8.51281 19.515C7.84811 20.0011 6.93012 19.8736 6.42312 19.2246C6.1662 18.8959 6.0577 18.4873 6.11739 18.0743C6.16781 17.725 6.33277 17.4128 6.58809 17.1781L9.68854 14.9476C9.85109 14.8307 9.88819 14.604 9.77163 14.4415C9.65466 14.2785 9.42839 14.2414 9.26544 14.3576L6.31422 16.4674C6.25009 16.513 6.18919 16.5614 6.13111 16.6126L5.43495 17.1136C4.80614 17.5657 3.9313 17.4491 3.44326 16.8474C3.18916 16.5344 3.07703 16.1415 3.12745 15.7414C3.17827 15.3413 3.38438 14.9888 3.70826 14.7488L3.96196 14.5613C4.00552 14.5334 4.04867 14.5036 4.09102 14.4721L7.40968 12.0065L7.41089 12.0057C7.57183 11.8859 7.60571 11.6588 7.48632 11.4979C7.36693 11.337 7.13945 11.3031 6.97852 11.4225L3.55297 13.9607C2.95966 14.3241 2.18042 14.2019 1.72868 13.6562C1.48022 13.3557 1.3689 12.9769 1.41488 12.5897C1.46086 12.2025 1.65809 11.8605 1.97028 11.627Z" 
                                                    fill={theme.palette.white}
                                                />
                                            </svg>
                                        </Box>
                                        <Box className={classes.itemTextContainer}>
                                            <Typography
                                                variant={TypographyVariants.H5}
                                                className={classes.itemText}
                                                dangerouslySetInnerHTML={{
                                                    __html: 'Community Support' 
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                            <Box
                                style={{
                                    height: '300px' 
                                }}
                            />
                        </Box>
                    </Slide>
                </Box>
            </Card>
        </CardContainer >
    );
};

export default PillarsIntro;
